import { useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import { Menu as MenuIcon } from 'lucide-react';
import type { View } from '../pages/LgmProductNavigator';
import { useValidationJobsSummary } from 'shared/hooks/useValidationJobsSummary';
import { useResponsiveNavOverflow } from 'shared/hooks/useResponsiveNavOverflow';
import { useViewportSize } from 'shared/hooks/useViewportSize';
import { LGM_ORANGE } from 'theme/colors';
import { GRAY_500 } from 'theme/grayScale';

// LGM Intelligence Hub — landing portal for the Explorer ecosystem
// (www.lgmexplorer.com). The Navigator's "home" glyph links here so the app
// sits seamlessly within the suite alongside Explorer, Architecture,
// Ontology, and the Data Catalog.
const INTEL_HUB_URL = 'https://www.lgmexplorer.com/lgm-intelligence-hub.html';

interface DefaultNavItem {
  key: View | 'hub';
  label: string;
  adminOnly?: boolean;
}

// Priority order -- when the viewport is too narrow to fit all of these,
// the ones at the end spill into the overflow menu first.
const DEFAULT_NAV_ITEMS: DefaultNavItem[] = [
  { key: 'hub', label: 'Hub' },
  { key: 'products', label: 'Products' },
  { key: 'manufacturer_master', label: 'Manufacturers' },
  { key: 'grades', label: 'Product Types', adminOnly: true },
  { key: 'therapeutic_areas', label: 'Therapeutic Areas', adminOnly: true },
  { key: 'manufacturer_map', label: 'Facilities Map' },
  { key: 'dashboard_metrics', label: 'Metrics' },
];

interface FixedOverflowItem {
  key: View;
  label: string;
  adminOnly?: boolean;
}

// Always in the overflow menu, regardless of available width.
const FIXED_OVERFLOW_ITEMS: FixedOverflowItem[] = [
  { key: 'audit_logs', label: 'Audit Logs', adminOnly: true },
  { key: 'cas_quarantine_queue', label: 'CAS Quarantine' },
  { key: 'product_upload_batches', label: 'Product Uploads', adminOnly: true },
];

export interface PrimaryNavProps {
  view: View;
  setView: (view: View) => void;
  isAdmin: boolean;
  showDevTools: boolean;
  onToggleDevTools: () => void;
  onLogout: () => void;
}

/**
 * Top nav bar's tab row + "More" overflow menu. Splits nav destinations
 * into two priority tiers:
 *  - `DEFAULT_NAV_ITEMS`: shown as plain buttons while there's room: falls
 *    back to the overflow menu, lowest-priority (rightmost) first, on a
 *    viewport too narrow to fit all of them (`useResponsiveNavOverflow`).
 *  - `FIXED_OVERFLOW_ITEMS` + Validation Jobs + Show/Hide Dev Tools +
 *    Logout: always in the overflow menu, at every viewport width.
 *
 * Validation Jobs is admin-RBAC-gated only (no A/B flag) and rendered as a
 * plain text menu item with its active-job count inline, same shape as the
 * other overflow tabs -- it used to be a separate icon+spinner widget
 * docked next to Show Dev Tools/Logout, gated by the (now removed)
 * `validate_products_devtool` A/B flag.
 */
const PrimaryNav: React.FC<PrimaryNavProps> = ({
  view,
  setView,
  isAdmin,
  showDevTools,
  onToggleDevTools,
  onLogout,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { activeCount, error: validationJobsError } =
    useValidationJobsSummary(isAdmin);
  const { heightClass } = useViewportSize();
  const isSmall = heightClass === 'sm';

  const defaultItems = DEFAULT_NAV_ITEMS.filter(
    item => !item.adminOnly || isAdmin
  );
  const { containerRef, probeRef, visibleCount } = useResponsiveNavOverflow(
    defaultItems.length
  );
  const shownItems = defaultItems.slice(0, visibleCount);
  const spilledItems = defaultItems.slice(visibleCount);

  const fixedOverflowItems = FIXED_OVERFLOW_ITEMS.filter(
    item => !item.adminOnly || isAdmin
  );

  const activeNavStyle: React.CSSProperties = isSmall
    ? { ...styles.navButtonActiveStyle, fontSize: '0.875rem' }
    : styles.navButtonActiveStyle;

  const closeMenu = (): void => setAnchorEl(null);

  const renderDefaultButton = (item: DefaultNavItem): React.ReactElement => {
    const { key } = item;
    return key === 'hub' ? (
      <Box
        component="a"
        key="hub"
        href={INTEL_HUB_URL}
        title="Return to LGM Intelligence Hub"
        aria-label="LGM Intelligence Hub — Explorer ecosystem home"
        style={styles.navHomeStyle}
        sx={navHomeSx}>
        <svg
          viewBox="0 0 24 24"
          style={{
            width: '13px',
            height: '13px',
            fill: 'currentColor',
            flexShrink: 0,
          }}
          aria-hidden="true">
          <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
        </svg>
        Hub
      </Box>
    ) : (
      <Box
        component="button"
        key={key}
        onClick={() => setView(key)}
        style={view === key ? activeNavStyle : styles.navButtonStyle}
        sx={navButtonSx}>
        {item.label}
      </Box>
    );
  };

  const validationJobsLabel = validationJobsError
    ? 'Validation Jobs (unavailable)'
    : activeCount > 0
      ? `Validation Jobs (${activeCount})`
      : 'Validation Jobs';

  return (
    <div style={styles.navContainerStyle}>
      <div
        ref={containerRef}
        style={styles.visibleRowStyle}
        data-testid="nav-primary-row">
        {/* Off-screen probes -- measure natural widths only, never shown. */}
        <div ref={probeRef} aria-hidden="true" style={styles.probeRowStyle}>
          {defaultItems.map(item => renderDefaultButton(item))}
        </div>

        {shownItems.map(item => renderDefaultButton(item))}
      </div>

      <IconButton
        size="small"
        onClick={e => setAnchorEl(e.currentTarget)}
        aria-label="More navigation options"
        aria-haspopup="true"
        title="More"
        data-testid="nav-overflow-menu-button">
        <MenuIcon size={18} />
      </IconButton>
      {/* keepMounted -- the Validation Jobs item's active-job count needs
          to be readable (and pollable by e2e) even while the menu is
          closed, not just re-fetched fresh on every open. */}
      <Menu
        keepMounted
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}>
        {spilledItems.map(item => {
          const { key } = item;
          return key === 'hub' ? (
            <MenuItem
              key="hub"
              component="a"
              href={INTEL_HUB_URL}
              onClick={closeMenu}
              data-testid="nav-overflow-item-hub">
              Hub
            </MenuItem>
          ) : (
            <MenuItem
              key={key}
              selected={view === key}
              onClick={() => {
                setView(key);
                closeMenu();
              }}
              data-testid={`nav-overflow-item-${key}`}>
              {item.label}
            </MenuItem>
          );
        })}
        {spilledItems.length > 0 && <Divider />}

        {fixedOverflowItems.map(item => (
          <MenuItem
            key={item.key}
            selected={view === item.key}
            onClick={() => {
              setView(item.key);
              closeMenu();
            }}
            data-testid={`nav-overflow-item-${item.key}`}>
            {item.label}
          </MenuItem>
        ))}
        {isAdmin && (
          <MenuItem
            selected={view === 'validation_jobs'}
            onClick={() => {
              setView('validation_jobs');
              closeMenu();
            }}
            data-testid="nav-overflow-item-validation-jobs">
            {validationJobsLabel}
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            onToggleDevTools();
            closeMenu();
          }}
          data-testid="nav-overflow-item-dev-tools">
          {showDevTools ? 'Hide Dev Tools' : 'Show Dev Tools'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            onLogout();
            closeMenu();
          }}
          data-testid="nav-overflow-item-logout">
          Logout
        </MenuItem>
      </Menu>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  navContainerStyle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flex: 1,
    minWidth: 0,
  } as React.CSSProperties,
  visibleRowStyle: {
    display: 'flex',
    alignItems: 'center',
    gap: '1.5rem', // space-x-6 -- useResponsiveNavOverflow reads this gap live via getComputedStyle
    color: GRAY_500, // text-gray-500
    fontSize: '0.875rem', // text-sm
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    flexWrap: 'nowrap',
  } as React.CSSProperties,
  probeRowStyle: {
    display: 'flex',
    alignItems: 'center',
    gap: '1.5rem',
    position: 'absolute',
    visibility: 'hidden',
    top: 0,
    left: 0,
    pointerEvents: 'none',
  } as React.CSSProperties,
  navHomeStyle: {
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: '5px 10px',
    borderRadius: '6px',
    background: 'rgba(241,90,34,0.12)', // LGM_ORANGE tint
    color: LGM_ORANGE,
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    flexShrink: 0,
  } as React.CSSProperties,
  navButtonStyle: {
    color: GRAY_500, // text-gray-500
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  } as React.CSSProperties,
  navButtonActiveStyle: {
    color: '#ea580c', // text-orange-600
    fontWeight: 'bold',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1.25rem',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  } as React.CSSProperties,
};

// Pseudo-state styling for the two plain (non-MUI-component) nav controls
// above -- the permitted MUI `sx` exception for `:hover`/`:active`/
// `:focus-visible`, which plain inline styles can't express (see
// copilot-instructions.md "Frontend conventions"). Static values stay in
// `styles` above; only the pseudo-states live here.
const navHomeSx = {
  '&:hover': { backgroundColor: 'rgba(241,90,34,0.22)' }, // LGM_ORANGE tint
  '&:focus-visible': {
    backgroundColor: 'rgba(241,90,34,0.22)',
    outline: `2px solid ${LGM_ORANGE}`,
    outlineOffset: '2px',
  },
  '&:active': { backgroundColor: 'rgba(241,90,34,0.30)' },
};

const navButtonSx = {
  '&:hover': { color: LGM_ORANGE },
  '&:focus-visible': {
    outline: `2px solid ${LGM_ORANGE}`,
    outlineOffset: '2px',
    borderRadius: '4px',
  },
  '&:active': { color: '#c2410c' },
};

export default PrimaryNav;
